import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { fmtDate, fmtTime } from "@/lib/time";
import { Plus, Search, Phone, Building2, User, Scale, X, ChevronRight,
         Mail, MessageCircle, Receipt, Clock, CheckCircle2, XCircle, MinusCircle,
         ReceiptText, TrendingUp } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => n ? `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : "—";

const PROSPECT_TYPE_ICONS = { law_firm: Building2, individual_lawyer: Scale, individual: User };
const PROSPECT_TYPE_COLORS = {
  law_firm:           "bg-violet-500/10 text-violet-600 border-violet-500/20",
  individual_lawyer:  "bg-blue-500/10 text-blue-600 border-blue-500/20",
  individual:         "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
};
const PROSPECT_TYPE_LABELS = { law_firm: "Law Firm", individual_lawyer: "Individual Lawyer", individual: "Individual" };

const DEAL_STAGES = ["prospect", "qualified", "demo_scheduled", "proposal_sent", "negotiation", "closed_won", "closed_lost"];
const STAGE_LABELS = {
  prospect: "Prospect", qualified: "Qualified", demo_scheduled: "Demo", proposal_sent: "Proposal",
  negotiation: "Negotiation", closed_won: "Won", closed_lost: "Lost",
};
const STAGE_COLORS = {
  prospect: "bg-muted text-muted-foreground", qualified: "bg-cyan-500/10 text-cyan-600",
  demo_scheduled: "bg-blue-500/10 text-blue-600", proposal_sent: "bg-violet-500/10 text-violet-600",
  negotiation: "bg-amber-500/10 text-amber-600", closed_won: "bg-emerald-500/10 text-emerald-600",
  closed_lost: "bg-red-500/10 text-red-500",
};

const EMPTY_PROSPECT = { name: "", prospect_type: "law_firm", email: "", phone: "", city: "", state: "", firm_name: "", estimated_value: "", notes: "", plan_id: "", billing_cycle: "monthly" };
const EMPTY_DEAL = { prospect_id: "", title: "", stage: "prospect", deal_value: "", notes: "", plan_id: "", billing_cycle: "monthly" };

export default function SMCRM() {
  const [tab, setTab] = useState("prospects");
  const [prospects, setProspects] = useState([]);
  const [deals, setDeals] = useState([]);
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_PROSPECT);
  const [dealForm, setDealForm] = useState(EMPTY_DEAL);
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [payHistory, setPayHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, dRes, pipRes, planRes] = await Promise.all([
        axios.get(`${API}/api/sm/crm/prospects`, { params: { limit: 100 }, withCredentials: true }),
        axios.get(`${API}/api/sm/crm/deals`, { params: { limit: 100 }, withCredentials: true }),
        axios.get(`${API}/api/sm/crm/pipeline-summary`, { withCredentials: true }),
        axios.get(`${API}/api/sm/subscriptions/plans`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setProspects(pRes.data.prospects || []);
      setDeals(dRes.data.deals || []);
      setPipeline(pipRes.data);
      setPlans(Array.isArray(planRes.data) ? planRes.data : []);
    } catch { toast.error("Failed to load CRM data"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredProspects = prospects.filter(p =>
    !search || p.name?.toLowerCase().includes(search.toLowerCase()) || p.email?.toLowerCase().includes(search.toLowerCase())
  );
  const filteredDeals = deals.filter(d =>
    (!stageFilter || d.stage === stageFilter) &&
    (!search || d.title?.toLowerCase().includes(search.toLowerCase()) || d.prospect_name?.toLowerCase().includes(search.toLowerCase()))
  );

  const handleCall = async (prospectId, name) => {
    try {
      const r = await axios.get(`${API}/api/sm/crm/prospects/${prospectId}/call`, { withCredentials: true });
      window.location.href = r.data.tel_url;
      toast.success(`Calling ${name}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Call failed"); }
  };

  const handleAddProspect = async (e) => {
    e.preventDefault(); setSaving(true);
    const payload = { ...form, estimated_value: form.estimated_value !== "" ? parseFloat(form.estimated_value) : null };
    try {
      const res = await axios.post(`${API}/api/sm/crm/prospects`, payload, { withCredentials: true });
      const notify = res.data?.notify || {};
      const msgs = [];
      if (notify.payment_link_url) msgs.push("payment link generated");
      if (notify.email_sent) msgs.push("email sent");
      if (notify.whatsapp_sent) msgs.push("WhatsApp sent");
      toast.success(`Prospect added${msgs.length ? " — " + msgs.join(", ") : ""}`);
      setShowAdd(false); setForm(EMPTY_PROSPECT); loadData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map(d => d.msg).join("; ") : "Failed");
    }
    finally { setSaving(false); }
  };

  const handleAddDeal = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await axios.post(`${API}/api/sm/crm/deals`,
        { ...dealForm, deal_value: parseFloat(dealForm.deal_value) || null }, { withCredentials: true });
      toast.success("Deal created"); setShowAdd(false); setDealForm(EMPTY_DEAL); loadData();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const openDealPanel = async (deal) => {
    setSelectedDeal(deal);
    setPayHistory([]);
    setHistoryLoading(true);
    try {
      const r = await axios.get(`${API}/api/sm/crm/deals/${deal.id}/payment-history`, { withCredentials: true });
      setPayHistory(r.data.notifications || []);
    } catch { setPayHistory([]); }
    finally { setHistoryLoading(false); }
  };

  const handleStageChange = async (dealId, stage) => {    if (stage === "closed_won" || stage === "closed_lost") {
      toast.info("Use Close Won / Close Lost actions"); return;
    }
    try {
      await axios.patch(`${API}/api/sm/crm/deals/${dealId}/stage`, { stage }, { withCredentials: true });
      loadData();
    } catch (err) { toast.error("Stage update failed"); }
  };

  const TypeBadge = ({ type }) => {
    const Icon = PROSPECT_TYPE_ICONS[type] || User;
    return (
      <span className={`text-[9px] uppercase tracking-[0.16em] border rounded-full px-2 py-0.5 font-semibold flex items-center gap-1 w-fit ${PROSPECT_TYPE_COLORS[type] || "bg-muted text-muted-foreground border-border"}`}>
        <Icon className="w-2.5 h-2.5" />{PROSPECT_TYPE_LABELS[type] || type}
      </span>
    );
  };

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="sm-crm">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-foreground">CRM & Prospects</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Law firms · Lawyers · Individuals</p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="add-prospect-btn"
          className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-xl text-[13px] font-medium hover:opacity-90 transition-all shrink-0">
          <Plus className="w-4 h-4" />Add Prospect
        </button>
      </div>

      {/* Pipeline summary */}
      {pipeline && (
        <div className="flex items-center gap-1 overflow-x-auto pb-1">
          {DEAL_STAGES.filter(s => !s.startsWith("closed")).map((s, i) => (
            <div key={s} className="flex items-center gap-1 shrink-0">
              <button onClick={() => setStageFilter(stageFilter === s ? "" : s)}
                className={`flex flex-col px-3 py-2 rounded-xl border transition-all ${stageFilter === s ? "border-primary/40 bg-primary/5" : "border-border bg-card hover:bg-muted/50"}`}>
                <span className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">{STAGE_LABELS[s]}</span>
                <span className="text-[15px] font-bold text-foreground">{pipeline.stages?.[s]?.count || 0}</span>
                <span className="text-[10px] text-muted-foreground">{fmt(pipeline.stages?.[s]?.value)}</span>
              </button>
              {i < DEAL_STAGES.filter(s => !s.startsWith("closed")).length - 1 && (
                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex bg-muted rounded-xl p-1 gap-1 w-fit">
        {["prospects", "deals"].map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`crm-tab-${t}`}
            className={`px-4 py-2 rounded-lg text-[12px] font-medium capitalize transition-all ${tab === t ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}>
            {t} {t === "prospects" ? `(${prospects.length})` : `(${deals.length})`}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative w-full sm:max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder={`Search ${tab}…`}
          className="w-full pl-8 pr-3 py-2 bg-card border border-border rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-primary/50" />
      </div>

      {tab === "prospects" && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-[13px] min-w-[680px]">
            <thead className="border-b border-border bg-muted/20">
              <tr>{["Name", "Type", "Plan Interest", "City", "Est. Value", "Deals", "Actions"].map(h => (
                <th key={h} className="px-5 py-3.5 text-left text-[9px] uppercase tracking-[0.18em] text-muted-foreground font-medium whitespace-nowrap">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="py-10 text-center text-muted-foreground animate-pulse">Loading…</td></tr>}
              {!loading && filteredProspects.map(p => {
                const plan = plans.find(pl => pl.id === p.plan_id);
                return (
                  <tr key={p.id} className="border-b border-border/50 hover:bg-muted/20 group">
                    <td className="px-5 py-3.5">
                      <div className="font-medium text-foreground">{p.name}</div>
                      {p.email && <div className="text-muted-foreground text-[11px]">{p.email}</div>}
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap"><TypeBadge type={p.prospect_type} /></td>
                    <td className="px-5 py-3.5 whitespace-nowrap">
                      {plan
                        ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 font-medium">{plan.name}</span>
                        : <span className="text-[11px] text-muted-foreground">—</span>}
                    </td>
                    <td className="px-5 py-3.5 text-muted-foreground whitespace-nowrap">{p.city || "—"}</td>
                    <td className="px-5 py-3.5 text-muted-foreground whitespace-nowrap">{fmt(p.estimated_value)}</td>
                    <td className="px-5 py-3.5 text-center text-muted-foreground">{p.deal_count || 0}</td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                        {p.phone_masked && (
                          <button onClick={() => handleCall(p.id, p.name)}
                            data-testid={`call-btn-${p.id}`}
                            title={`Call (${p.phone_masked})`}
                            className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 border border-emerald-500/30 text-emerald-600 rounded-lg hover:bg-emerald-500/10 transition-all">
                            <Phone className="w-3 h-3" />Call
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && filteredProspects.length === 0 && (
                <tr><td colSpan={7} className="py-12 text-center text-muted-foreground text-sm">
                  No prospects yet. Add your first prospect to start building the pipeline.
                </td></tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Deals Table */}
      {tab === "deals" && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-[13px] min-w-[560px]">
            <thead className="border-b border-border bg-muted/20">
              <tr>{["Deal Title", "Prospect", "Stage", "Value", "Stage Actions"].map(h => (
                <th key={h} className="px-5 py-3.5 text-left text-[9px] uppercase tracking-[0.18em] text-muted-foreground font-medium whitespace-nowrap">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="py-10 text-center text-muted-foreground animate-pulse">Loading…</td></tr>}
              {!loading && filteredDeals.map(d => (
                <tr key={d.id} onClick={() => openDealPanel(d)}
                  className="border-b border-border/50 hover:bg-muted/20 group cursor-pointer transition-colors">
                  <td className="px-5 py-3.5 font-medium text-foreground whitespace-nowrap">{d.title}</td>
                  <td className="px-5 py-3.5">
                    <div className="text-muted-foreground whitespace-nowrap">{d.prospect_name || "—"}</div>
                    {d.prospect_type && <TypeBadge type={d.prospect_type} />}
                  </td>
                  <td className="px-5 py-3.5 whitespace-nowrap">
                    <span className={`text-[9px] uppercase tracking-[0.16em] rounded-full px-2 py-0.5 font-semibold ${STAGE_COLORS[d.stage] || "bg-muted"}`}>
                      {STAGE_LABELS[d.stage] || d.stage}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-muted-foreground whitespace-nowrap">{fmt(d.deal_value)}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex gap-1 flex-wrap opacity-0 group-hover:opacity-100 transition-all">
                      {d.stage !== "closed_won" && d.stage !== "closed_lost" && DEAL_STAGES.filter(s => !s.startsWith("closed") && s !== d.stage).slice(0, 2).map(s => (
                        <button key={s} onClick={(e) => { e.stopPropagation(); handleStageChange(d.id, s); }}
                          className="text-[10px] px-2 py-1 border border-border rounded-lg text-muted-foreground hover:bg-muted transition-all whitespace-nowrap">
                          → {STAGE_LABELS[s]}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && filteredDeals.length === 0 && (
                <tr><td colSpan={5} className="py-12 text-center text-muted-foreground text-sm">No deals yet.</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Add Prospect/Deal Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card">
              <div className="flex gap-3">
                {["prospects", "deals"].map(t => (
                  <button key={t} onClick={() => { setTab(t); setShowAdd(true); }}
                    className={`text-[12px] font-medium capitalize transition-all ${tab === t ? "text-primary" : "text-muted-foreground"}`}>
                    New {t.slice(0, -1)}
                  </button>
                ))}
              </div>
              <button onClick={() => setShowAdd(false)}><X className="w-4 h-4 text-muted-foreground" /></button>
            </div>

            {tab === "prospects" ? (
              <form onSubmit={handleAddProspect} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground block mb-1">Prospect Type *</label>
                    <div className="flex gap-2">
                      {Object.entries(PROSPECT_TYPE_LABELS).map(([k, v]) => (
                        <button key={k} type="button" onClick={() => setForm(f => ({ ...f, prospect_type: k }))}
                          className={`flex-1 py-2 rounded-xl text-[11px] border font-medium transition-all ${form.prospect_type === k ? "bg-primary/10 border-primary/40 text-primary" : "border-border text-muted-foreground hover:bg-muted"}`}>
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>
                  {[["name", "Name *", true], ["email", "Email", false], ["phone", "Phone (hidden from lists)", false],
                    ["city", "City", false], ["state", "State", false],
                    ...(form.prospect_type === "law_firm" ? [["firm_name", "Firm Name", false]] : []),
                    ...(form.prospect_type === "individual_lawyer" ? [["bar_council_number", "Bar Council No.", false]] : []),
                    ["estimated_value", "Estimated Value (₹)", false]].map(([key, label, req]) => (
                    <div key={key} className={key === "name" ? "col-span-2" : ""}>
                      <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground block mb-1">{label}</label>
                      <input required={req} value={form[key] || ""} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                        type={key === "estimated_value" ? "number" : "text"}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-primary/50" />
                    </div>
                  ))}
                  <div className="col-span-2 space-y-2">
                    <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground block">Subscription Plan</label>
                    {/* Billing cycle toggle */}
                    <div className="flex gap-1 p-0.5 bg-muted/30 border border-border rounded-lg w-fit">
                      {["monthly", "annual"].map(cycle => (
                        <button key={cycle} type="button"
                          onClick={() => setForm(f => ({ ...f, billing_cycle: cycle }))}
                          className={`px-4 py-1.5 rounded-md text-[11px] font-medium transition-all ${
                            form.billing_cycle === cycle
                              ? "bg-primary text-primary-foreground shadow-sm"
                              : "text-muted-foreground hover:text-foreground"
                          }`}>
                          {cycle === "monthly" ? "Monthly" : "Annual"}
                          {cycle === "annual" && plans.some(p => p.price_annual > 0) && (
                            <span className="ml-1.5 text-[9px] bg-emerald-500/20 text-emerald-600 px-1.5 py-0.5 rounded-full">Save 20%</span>
                          )}
                        </button>
                      ))}
                    </div>
                    {/* Plan selector */}
                    <select value={form.plan_id} onChange={e => setForm(f => ({ ...f, plan_id: e.target.value }))}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-primary/50">
                      <option value="">— No plan selected —</option>
                      {plans.map(p => {
                        const price = form.billing_cycle === "annual" && p.price_annual > 0
                          ? `₹${Number(p.price_annual).toLocaleString("en-IN")}/yr`
                          : `₹${Number(p.price_monthly).toLocaleString("en-IN")}/mo`;
                        return <option key={p.id} value={p.id}>{`${p.name} — ${price}`}</option>;
                      })}
                    </select>
                    {/* Savings callout */}
                    {form.plan_id && form.billing_cycle === "annual" && (() => {
                      const p = plans.find(pl => pl.id === form.plan_id);
                      if (p && p.price_annual > 0 && p.price_monthly > 0) {
                        const saved = (p.price_monthly * 12) - p.price_annual;
                        return saved > 0
                          ? <div className="text-[11px] text-emerald-600 bg-emerald-500/8 border border-emerald-500/20 rounded px-2.5 py-1.5">
                              Annual billing saves ₹{Number(saved).toLocaleString("en-IN")}/year vs monthly
                            </div>
                          : null;
                      }
                    })()}
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground block mb-1">Notes</label>
                    <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-primary/50 resize-none" />
                  </div>
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={() => setShowAdd(false)} className="flex-1 py-2.5 border border-border rounded-xl text-[13px] text-muted-foreground hover:bg-muted">Cancel</button>
                  <button type="submit" disabled={saving} data-testid="save-prospect-btn" className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-xl text-[13px] font-medium hover:opacity-90 disabled:opacity-50">
                    {saving ? "Saving…" : "Add Prospect"}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleAddDeal} className="p-6 space-y-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground block mb-1">Prospect *</label>
                  <select value={dealForm.prospect_id} onChange={e => setDealForm(f => ({ ...f, prospect_id: e.target.value }))} required
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-primary/50">
                    <option value="">— Select a prospect —</option>
                    {prospects.map(p => <option key={p.id} value={p.id}>{p.name} ({PROSPECT_TYPE_LABELS[p.prospect_type]})</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground block mb-1">Deal Title *</label>
                  <input value={dealForm.title} onChange={e => setDealForm(f => ({ ...f, title: e.target.value }))} required
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-primary/50" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground block mb-1">Initial Stage</label>
                    <select value={dealForm.stage} onChange={e => setDealForm(f => ({ ...f, stage: e.target.value }))}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-primary/50">
                      {DEAL_STAGES.filter(s => !s.startsWith("closed")).map(s => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground block mb-1">Expected Value (₹)</label>
                    <input type="number" value={dealForm.deal_value} onChange={e => setDealForm(f => ({ ...f, deal_value: e.target.value }))}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-primary/50" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground block mb-1">Subscription Plan</label>
                  {/* Billing cycle toggle */}
                  <div className="flex gap-1 p-0.5 bg-muted/30 border border-border rounded-lg w-fit mb-2">
                    {["monthly", "annual"].map(cycle => (
                      <button key={cycle} type="button"
                        onClick={() => setDealForm(f => ({ ...f, billing_cycle: cycle }))}
                        className={`px-3 py-1 rounded-md text-[11px] font-medium transition-all ${
                          dealForm.billing_cycle === cycle
                            ? "bg-primary text-primary-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground"
                        }`}>
                        {cycle === "monthly" ? "Monthly" : "Annual"}
                      </button>
                    ))}
                  </div>
                  <select value={dealForm.plan_id} onChange={e => setDealForm(f => ({ ...f, plan_id: e.target.value }))}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-primary/50">
                    <option value="">— No plan selected —</option>
                    {plans.map(p => {
                      const price = dealForm.billing_cycle === "annual" && p.price_annual > 0
                        ? `₹${Number(p.price_annual).toLocaleString("en-IN")}/yr`
                        : `₹${Number(p.price_monthly).toLocaleString("en-IN")}/mo`;
                      return <option key={p.id} value={p.id}>{`${p.name} — ${price}`}</option>;
                    })}
                  </select>
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={() => setShowAdd(false)} className="flex-1 py-2.5 border border-border rounded-xl text-[13px] text-muted-foreground hover:bg-muted">Cancel</button>
                  <button type="submit" disabled={saving} data-testid="save-deal-btn" className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-xl text-[13px] font-medium hover:opacity-90 disabled:opacity-50">
                    {saving ? "Saving…" : "Create Deal"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Deal Detail Panel — slides in from right */}
      {selectedDeal && (
        <div className="fixed inset-0 z-50 flex" data-testid="deal-detail-panel">
          {/* Backdrop */}
          <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={() => setSelectedDeal(null)} />

          {/* Panel */}
          <div className="w-full max-w-md bg-card border-l border-border shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-right duration-200">

            {/* Panel header */}
            <div className="px-6 py-4 border-b border-border bg-muted/30 flex items-start justify-between shrink-0">
              <div className="flex-1 min-w-0">
                <p className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground mb-1">Deal</p>
                <h2 className="font-semibold text-foreground text-[15px] leading-tight truncate">{selectedDeal.title}</h2>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className={`text-[9px] uppercase tracking-[0.14em] rounded-full px-2 py-0.5 font-semibold ${STAGE_COLORS[selectedDeal.stage] || "bg-muted"}`}>
                    {STAGE_LABELS[selectedDeal.stage] || selectedDeal.stage}
                  </span>
                  {selectedDeal.deal_value && (
                    <span className="text-[11px] text-muted-foreground font-medium">{fmt(selectedDeal.deal_value)}</span>
                  )}
                </div>
              </div>
              <button onClick={() => setSelectedDeal(null)} data-testid="close-deal-panel"
                className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground ml-3 shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Prospect info */}
            {selectedDeal.prospect_name && (
              <div className="px-6 py-3 border-b border-border/50 bg-muted/10 shrink-0">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-3.5 h-3.5 text-primary" />
                  </div>
                  <div>
                    <p className="text-[13px] font-medium text-foreground">{selectedDeal.prospect_name}</p>
                    {selectedDeal.prospect_type && (
                      <p className="text-[10px] text-muted-foreground capitalize">{selectedDeal.prospect_type.replace("_", " ")}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Payment History section */}
            <div className="flex-1 overflow-y-auto">
              <div className="px-6 py-4 border-b border-border/50 shrink-0">
                <div className="flex items-center gap-2">
                  <ReceiptText className="w-4 h-4 text-primary" />
                  <h3 className="text-[13px] font-semibold text-foreground">Payment History</h3>
                  {payHistory.length > 0 && (
                    <span className="text-[9px] uppercase tracking-wider bg-primary/10 text-primary px-2 py-0.5 rounded-full font-semibold">
                      {payHistory.length}
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">All receipts sent for this deal</p>
              </div>

              <div className="p-6 space-y-3">
                {historyLoading && (
                  <div className="flex flex-col gap-3">
                    {[1, 2].map(i => (
                      <div key={i} className="bg-muted/30 border border-border rounded-xl p-4 animate-pulse">
                        <div className="h-3 bg-muted rounded w-32 mb-2" />
                        <div className="h-2.5 bg-muted rounded w-24" />
                      </div>
                    ))}
                  </div>
                )}

                {!historyLoading && payHistory.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-10 text-center" data-testid="no-payment-history">
                    <div className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center mb-3">
                      <Receipt className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <p className="text-[13px] font-medium text-foreground">No receipts sent yet</p>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Notifications will appear here after payment confirmation.
                    </p>
                  </div>
                )}

                {!historyLoading && payHistory.map((n, idx) => (
                  <div key={n.id || idx} data-testid={`payment-history-row-${idx}`}
                    className="bg-background border border-border rounded-xl overflow-hidden hover:border-primary/30 transition-all">

                    {/* Amount + date row */}
                    <div className="px-4 py-3 flex items-center justify-between border-b border-border/40">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                          <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-[14px] font-bold text-foreground">
                            ₹{Number(n.amount_inr || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </p>
                          <p className="text-[10px] text-muted-foreground capitalize">
                            {n.kind === "prospect_deal" ? "Prospect Deal" : n.kind === "wallet_topup" ? "Wallet Top-up" : "Subscription"}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] text-muted-foreground flex items-center gap-1 justify-end">
                          <Clock className="w-3 h-3" />
                          {n.sent_at ? fmtDate(n.sent_at) : "—"}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {n.sent_at ? fmtTime(n.sent_at) : ""}
                        </p>
                      </div>
                    </div>

                    {/* Details */}
                    <div className="px-4 py-3 space-y-2.5">
                      {n.plan_name && (
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Plan</span>
                          <span className="text-[11px] font-medium text-foreground">{n.plan_name}</span>
                        </div>
                      )}
                      {n.payment_id && (
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Payment ID</span>
                          <span className="text-[10px] font-mono text-foreground truncate max-w-[140px]" title={n.payment_id}>{n.payment_id}</span>
                        </div>
                      )}
                      {n.email_masked && (
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Recipient</span>
                          <span className="text-[11px] text-muted-foreground">{n.email_masked}</span>
                        </div>
                      )}

                      {/* Dispatch status badges */}
                      <div className="flex items-center gap-2 pt-1">
                        <DispatchBadge icon={Mail} label="Email" sent={n.email_sent} />
                        <DispatchBadge icon={MessageCircle} label="WhatsApp" sent={n.whatsapp_sent} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t border-border bg-muted/20 shrink-0">
              <p className="text-[10px] text-muted-foreground text-center">
                All receipts signed: <strong>Team Law OS — Inno Aim Private Limited</strong>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DispatchBadge({ icon: Icon, label, sent }) {
  if (sent === true)
    return (
      <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 font-medium">
        <CheckCircle2 className="w-2.5 h-2.5" />{label}: Sent
      </span>
    );
  if (sent === false)
    return (
      <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 text-red-500 border border-red-500/20 font-medium">
        <XCircle className="w-2.5 h-2.5" />{label}: Failed
      </span>
    );
  return (
    <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border font-medium">
      <MinusCircle className="w-2.5 h-2.5" />{label}: —
    </span>
  );
}
